// ============================================================================
// ADD URL SOURCE DIALOG - Add a URL to the knowledge base
// ============================================================================

import { useState } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { knowledgeApi, type KnowledgeSource, type KnowledgeCategory } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES: { value: KnowledgeCategory; label: string; icon: string }[] = [
  { value: 'framework', label: 'Framework', icon: '🧩' },
  { value: 'case_study', label: 'Case Study', icon: '📖' },
  { value: 'best_practice', label: 'Best Practice', icon: '✅' },
  { value: 'template', label: 'Template', icon: '📋' },
  { value: 'research', label: 'Research', icon: '🔬' },
  { value: 'guide', label: 'Guide', icon: '📘' },
  { value: 'other', label: 'Other', icon: '📄' },
]

// ============================================================================
// COMPONENT
// ============================================================================

interface AddUrlSourceDialogProps {
  onClose: () => void
  onSourceCreated: (source: KnowledgeSource) => void
}

export function AddUrlSourceDialog({ onClose, onSourceCreated }: AddUrlSourceDialogProps) {
  const { accountId } = useSessionStore()
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<KnowledgeCategory>('other')
  const [tags, setTags] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!accountId) {
      setError('No account selected')
      return
    }

    if (!url.trim()) {
      setError('URL is required')
      return
    }

    // Validate URL
    try {
      new URL(url)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      const result = await knowledgeApi.addUrl(accountId, {
        url: url.trim(),
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        category,
        tags: tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
      })

      onSourceCreated(result.source)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add URL')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add URL Source</DialogTitle>
          <DialogDescription>
            Add a web page or document URL to your knowledge base. The content will be fetched and
            processed for RAG retrieval.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* URL */}
            <div className="space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/article"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
              />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title (optional)</Label>
              <Input
                id="title"
                placeholder="Leave blank to auto-detect from page"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={(val) => setCategory(val as KnowledgeCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the content"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (optional)</Label>
              <Input
                id="tags"
                placeholder="product-management, strategy, discovery"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <p className="text-xs text-zinc-500">Separate tags with commas</p>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add URL'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddUrlSourceDialog
